import React, { lazy } from 'react';
import { Task, FullProject } from '../../types';

const CreditsModal = lazy(() =>
  import('../CreditsModal').then((m) => ({ default: m.CreditsModal }))
);
const AllowedUsersModal = lazy(() =>
  import('../AllowedUsersModal').then((m) => ({ default: m.AllowedUsersModal }))
);
const CommandPalette = lazy(() =>
  import('../CommandPalette').then((m) => ({ default: m.CommandPalette }))
);
const ShortcutsHelpModal = lazy(() =>
  import('../ShortcutsHelpModal').then((m) => ({ default: m.ShortcutsHelpModal }))
);
const UserProfileModal = lazy(() =>
  import('../UserProfileModal').then((m) => ({ default: m.UserProfileModal }))
);
const CreditsAndHelpModal = lazy(() =>
  import('../CreditsAndHelpModal').then((m) => ({ default: m.CreditsAndHelpModal }))
);
const QuickAddTaskModal = lazy(() =>
  import('../QuickAddTaskModal').then((m) => ({ default: m.QuickAddTaskModal }))
);
const OnboardingWizard = lazy(() =>
  import('../OnboardingWizard').then((m) => ({ default: m.OnboardingWizard }))
);
const GuidedTour = lazy(() => import('../GuidedTour').then((m) => ({ default: m.GuidedTour })));
const DragDropHint = lazy(() =>
  import('../DragDropHint').then((m) => ({ default: m.DragDropHint }))
);
const PrivacyModal = lazy(() =>
  import('../PrivacyModal').then((m) => ({ default: m.PrivacyModal }))
);
const AccountDeletionModal = lazy(() =>
  import('../AccountDeletionModal').then((m) => ({ default: m.AccountDeletionModal }))
);

export interface SystemAndOnboardingModalsGroupProps {
  showPrivacyModal?: boolean;
  setShowPrivacyModal: (open: boolean) => void;
  showAccountDeletionModal?: boolean;
  setShowAccountDeletionModal: (open: boolean) => void;
  showCreditsModal?: boolean;
  setShowCreditsModal: (open: boolean) => void;
  showAllowedUsersModal?: boolean;
  setShowAllowedUsersModal: (open: boolean) => void;
  showCommandPalette?: boolean;
  setShowCommandPalette: (open: boolean) => void;
  showShortcutsHelpModal?: boolean;
  setShowShortcutsHelpModal: (open: boolean) => void;
  showUserProfileModal?: boolean;
  setShowUserProfileModal: (open: boolean) => void;
  showCreditsAndHelpModal?: boolean;
  setShowCreditsAndHelpModal: (open: boolean) => void;
  showQuickAddTaskModal?: boolean;
  setShowQuickAddTaskModal: (open: boolean) => void;
  showOnboardingWizard?: boolean;
  setShowOnboardingWizard: (open: boolean) => void;
  showGuidedTour?: boolean;
  setShowGuidedTour: (open: boolean) => void;
  showDragDropHint?: boolean;
  setShowDragDropHint: (open: boolean) => void;
  setShowProjectOverviewModal: (open: boolean) => void;
  setShowExportProModal: (open: boolean) => void;
  setShowAIPlanner: (open: boolean) => void;
  setCurrentProjectId: (id: string) => void;
  setEditingTask: (task: Task | null) => void;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  isDarkMode: boolean;
  viewMode: string;
  setViewMode: (mode: any) => void;
  kanbanMode: string;
  isRightSidebarOpen: boolean;
  setIsRightSidebarOpen: (open: boolean) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  allProjects: FullProject[];
  updateSampleProjectForBranch: (branchId: string) => Promise<void>;
  userEmail?: string;
  currentUserId?: string;
}

export const SystemAndOnboardingModalsGroup: React.FC<SystemAndOnboardingModalsGroupProps> = React.memo(({
  showPrivacyModal,
  setShowPrivacyModal,
  showAccountDeletionModal,
  setShowAccountDeletionModal,
  showCreditsModal,
  setShowCreditsModal,
  showAllowedUsersModal,
  setShowAllowedUsersModal,
  showCommandPalette,
  setShowCommandPalette,
  showShortcutsHelpModal,
  setShowShortcutsHelpModal,
  showUserProfileModal,
  setShowUserProfileModal,
  showCreditsAndHelpModal,
  setShowCreditsAndHelpModal,
  showQuickAddTaskModal,
  setShowQuickAddTaskModal,
  showOnboardingWizard,
  setShowOnboardingWizard,
  showGuidedTour,
  setShowGuidedTour,
  showDragDropHint,
  setShowDragDropHint,
  setShowProjectOverviewModal,
  setShowExportProModal,
  setShowAIPlanner,
  setCurrentProjectId,
  setEditingTask,
  setIsDarkMode,
  isDarkMode,
  viewMode,
  setViewMode,
  kanbanMode,
  isRightSidebarOpen,
  setIsRightSidebarOpen,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  allProjects,
  updateSampleProjectForBranch,
  userEmail,
  currentUserId,
}) => {
  return (
    <>
      {showPrivacyModal && (
        <PrivacyModal
          isOpen={showPrivacyModal}
          onClose={() => setShowPrivacyModal(false)}
          currentUserEmail={userEmail}
          currentUserId={currentUserId}
          onOpenAccountDeletion={() => {
            setShowPrivacyModal(false);
            setShowAccountDeletionModal(true);
          }}
        />
      )}

      {showAccountDeletionModal && (
        <AccountDeletionModal
          isOpen={showAccountDeletionModal}
          onClose={() => setShowAccountDeletionModal(false)}
        />
      )}

      {showCreditsModal && (
        <CreditsModal isOpen={showCreditsModal} onClose={() => setShowCreditsModal(false)} />
      )}

      {showAllowedUsersModal && (
        <AllowedUsersModal
          isOpen={showAllowedUsersModal}
          onClose={() => setShowAllowedUsersModal(false)}
        />
      )}

      {showCommandPalette && (
        <CommandPalette
          isOpen={showCommandPalette}
          onClose={() => setShowCommandPalette(false)}
          onOpenNewProject={() => setShowProjectOverviewModal(true)}
          onOpenExportPro={() => setShowExportProModal(true)}
          onOpenAIPlanner={() => setShowAIPlanner(true)}
          onSelectProject={(projId) => setCurrentProjectId(projId)}
          onSelectTask={(task, projId) => {
            setCurrentProjectId(projId);
            setEditingTask(task);
          }}
          onToggleDarkMode={() => setIsDarkMode((prev) => !prev)}
          onOpenShortcutsHelp={() => setShowShortcutsHelpModal(true)}
          isDarkMode={isDarkMode}
        />
      )}

      {showShortcutsHelpModal && (
        <ShortcutsHelpModal
          isOpen={showShortcutsHelpModal}
          onClose={() => setShowShortcutsHelpModal(false)}
        />
      )}

      {showUserProfileModal && (
        <UserProfileModal
          isOpen={showUserProfileModal}
          onClose={() => setShowUserProfileModal(false)}
          onOpenAllowedUsers={() => setShowAllowedUsersModal(true)}
          onOpenAccountDeletion={() => setShowAccountDeletionModal(true)}
        />
      )}

      {showCreditsAndHelpModal && (
        <CreditsAndHelpModal
          isOpen={showCreditsAndHelpModal}
          onClose={() => setShowCreditsAndHelpModal(false)}
        />
      )}

      {showQuickAddTaskModal && (
        <QuickAddTaskModal
          isOpen={showQuickAddTaskModal}
          onClose={() => setShowQuickAddTaskModal(false)}
          onOpenDetails={(task) => setEditingTask(task)}
          initialKanbanMode={viewMode === 'kanban' ? (kanbanMode as any) : 'status'}
        />
      )}

      {showOnboardingWizard && (
        <OnboardingWizard
          isOpen={showOnboardingWizard}
          onClose={() => setShowOnboardingWizard(false)}
          onSelectBranch={updateSampleProjectForBranch}
          onStartTour={() => setShowGuidedTour(true)}
        />
      )}

      {showGuidedTour && (
        <GuidedTour
          isOpen={showGuidedTour}
          onClose={() => setShowGuidedTour(false)}
          currentViewMode={viewMode}
          onChangeViewMode={(mode) => setViewMode(mode as any)}
          isRightSidebarOpen={isRightSidebarOpen}
          setIsRightSidebarOpen={setIsRightSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          onSelectProject={(id) => setCurrentProjectId(id)}
          sampleProjectId={allProjects.find((p) => p.isSample || p.metadata?.isSample)?.id}
        />
      )}

      {showDragDropHint && (
        <DragDropHint isVisible={showDragDropHint} onClose={() => setShowDragDropHint(false)} />
      )}
    </>
  );
});
